<template>
  <div class="occupations-list">
    <!-- Filters -->
    <div class="filters">
      <input
        v-model="search"
        @input="debouncedLoad"
        type="text"
        class="filter-input"
        :placeholder="$t('bo.esco.searchOccupations')"
      />
      <select v-model="withSector" @change="loadOccupations" class="filter-select">
        <option value="">{{ $t('bo.esco.allSectors') }}</option>
        <option value="true">{{ $t('bo.esco.withSector') }}</option>
        <option value="false">{{ $t('bo.esco.withoutSector') }}</option>
      </select>
      <select v-model="withOverride" @change="loadOccupations" class="filter-select">
        <option value="">{{ $t('bo.esco.allOverrides') }}</option>
        <option value="true">{{ $t('bo.esco.manuallyEdited') }}</option>
        <option value="false">{{ $t('bo.esco.notEdited') }}</option>
      </select>
    </div>

    <div v-if="loading" class="loading">{{ $t('bo.esco.loadingOccupations') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="occupations.length === 0" class="empty">{{ $t('bo.esco.noOccupationsFound') }}</div>

    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.esco.colTitle') }}</th>
          <th>{{ $t('bo.esco.colIscoCode') }}</th>
          <th>{{ $t('bo.esco.colSkills') }}</th>
          <th>{{ $t('bo.esco.colImportance') }}</th>
          <th>{{ $t('bo.esco.colLevel') }}</th>
          <th>{{ $t('bo.esco.colOverride') }}</th>
          <th>{{ $t('bo.common.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="occ in occupations" :key="occ.id">
          <td class="col-title">{{ occ.title }}</td>
          <td>{{ occ.isco_code || '—' }}</td>
          <td class="col-stats">
            <span v-if="occ.skills_count > 0" class="badge-count">{{ occ.skills_count }}</span>
            <span v-else class="text-muted">—</span>
          </td>
          <td class="col-importance">
            <template v-if="occ.avg_importance != null">
              <div class="mini-bar-wrap" :title="`avg ${Math.round(occ.avg_importance * 100)}% · ${occ.with_importance}/${occ.skills_count} skills`">
                <div class="mini-bar" :style="{ width: (occ.avg_importance * 100) + '%', background: importanceColor(occ.avg_importance) }"></div>
              </div>
              <span class="mini-val">{{ Math.round(occ.avg_importance * 100) }}%</span>
              <span v-if="occ.with_importance < occ.skills_count" class="coverage-hint">({{ occ.with_importance }}/{{ occ.skills_count }})</span>
            </template>
            <span v-else class="text-muted">—</span>
          </td>
          <td class="col-level">
            <template v-if="occ.avg_level != null">
              <span class="level-badge">{{ occ.avg_level }}<span class="level-max">/7</span></span>
              <span v-if="occ.with_level < occ.skills_count" class="coverage-hint">({{ occ.with_level }}/{{ occ.skills_count }})</span>
            </template>
            <span v-else class="text-muted">—</span>
          </td>
          <td>
            <span v-if="occ.manually_edited" class="badge-override">{{ $t('bo.esco.badgeEdited') }}</span>
          </td>
          <td>
            <button @click="openDetail(occ.id)" class="btn-edit">{{ $t('bo.common.edit') }}</button>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Pagination -->
    <div v-if="pagination.last_page > 1" class="pagination">
      <button :disabled="pagination.current_page === 1" @click="loadPage(pagination.current_page - 1)" class="page-btn">{{ $t('bo.common.previous') }}</button>
      <span class="page-info">{{ $t('bo.esco.pageInfo', { current: pagination.current_page, last: pagination.last_page, total: pagination.total }) }}</span>
      <button :disabled="pagination.current_page === pagination.last_page" @click="loadPage(pagination.current_page + 1)" class="page-btn">{{ $t('bo.common.next') }}</button>
    </div>

    <!-- Detail modal -->
    <OccupationDetail
      v-if="detailId"
      :occupation-id="detailId"
      @close="detailId = null"
      @updated="handleUpdated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()
import OccupationDetail from './OccupationDetail.vue'

const occupations = ref([])
const loading = ref(false)
const error = ref('')
const search = ref('')
const withSector = ref('')
const withOverride = ref('')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })
const detailId = ref(null)

let debounceTimer = null

const debouncedLoad = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(loadOccupations, 400)
}

const loadOccupations = async (page = 1) => {
  loading.value = true
  error.value = ''
  try {
    const params = { page, per_page: 50 }
    if (search.value) params.search = search.value
    if (withSector.value !== '') params.with_sector = withSector.value
    if (withOverride.value !== '') params.with_override = withOverride.value
    const res = await escoService.getOccupations(params)
    occupations.value = res.data?.data || []
    pagination.value = {
      current_page: res.data?.current_page || 1,
      last_page: res.data?.last_page || 1,
      total: res.data?.total || 0,
    }
  } catch {
    error.value = t('bo.esco.failedLoadOccupations')
  } finally {
    loading.value = false
  }
}

const loadPage = (page) => loadOccupations(page)

const importanceColor = (val) => {
  if (val >= 0.7) return '#10b981'
  if (val >= 0.4) return '#f59e0b'
  return '#ef4444'
}

const openDetail = (id) => {
  detailId.value = id
}

const handleUpdated = () => {
  loadOccupations(pagination.value.current_page)
}

onMounted(loadOccupations)
</script>

<style scoped>
.occupations-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.filters {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filter-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  flex: 1;
  min-width: 200px;
}

.filter-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
}

.loading, .empty {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td { border-bottom: none; }

.col-title {
  font-weight: 500;
  color: #1f2937;
  max-width: 280px;
}

.col-stats { white-space: nowrap; }

.badge-count {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.col-importance {
  white-space: nowrap;
  min-width: 110px;
}

.mini-bar-wrap {
  display: inline-block;
  width: 52px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  vertical-align: middle;
  margin-right: 0.3rem;
}

.mini-bar {
  height: 100%;
  border-radius: 9999px;
  transition: width 0.2s;
}

.mini-val {
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
  vertical-align: middle;
}

.col-level { white-space: nowrap; }

.level-badge {
  display: inline-block;
  padding: 0.15rem 0.45rem;
  background: #1e3a8a;
  color: #bfdbfe;
  border-radius: 0.25rem;
  font-size: 0.78rem;
  font-weight: 700;
}

.level-max { color: #93c5fd; font-weight: 400; }

.coverage-hint {
  font-size: 0.7rem;
  color: #9ca3af;
  margin-left: 0.2rem;
}

.badge-override {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  background: #fef3c7;
  color: #92400e;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.btn-edit {
  padding: 0.3rem 0.65rem;
  border: 1px solid #3b82f6;
  color: #3b82f6;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
}

.btn-edit:hover { background: #eff6ff; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:hover:not(:disabled) { background: #f9fafb; }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
